import React from 'react';
import { useTranslate } from "../../../../../customHooks";
import { Body, Card, CardBody, CardHeader, CopyButton, Link, Tooltip } from "@panwds/react-ui";
import { InfoIcon, CaretDownIcon, CaretRightIcon } from "@panwds/icons";
import { FormattedMessage } from "react-intl";
import { DOCUMENTATION_URLS } from './constants';
import { FirewallData, PrerequisiteRowProps } from './types';
import { POLICY_MANAGER, PolicyManagerType } from '../../../firewallsUtil';

const PrerequisiteRow = React.memo<PrerequisiteRowProps>(({ label, tooltipText, children, addPadding = false }) => (
    <div className={`tw-flex tw-flex-row tw-gap-8 tw-items-center ${addPadding ? 'tw-py-2' : ''}`}>
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-w-80">
            <Body addClassName="tw-text-xs tw-text-gray-600">{label}</Body>
            {tooltipText && (
                <Tooltip label={tooltipText}>
                    <InfoIcon size="sm" className="tw-text-disabled"/>
                </Tooltip>
            )}
        </div>
        <div className="tw-flex tw-flex-row tw-items-center">
            {children}
        </div>
    </div>
));

PrerequisiteRow.displayName = 'PrerequisiteRow';

interface PrerequisitesCardProps {
    isCollapsed: boolean;
    onToggle: () => void;
    firewallData: FirewallData;
    policyManager?: PolicyManagerType;
}

export const PrerequisitesCard: React.FC<PrerequisitesCardProps> = ({
    isCollapsed,
    onToggle,
    firewallData,
    policyManager,
}) => {
    const translate = useTranslate();
    const isSCM = policyManager === POLICY_MANAGER.STRATA;

    return (
        <Card>
            <div onClick={onToggle} className="tw-cursor-pointer">
                <CardHeader
                    addClassName={`tw-border-b-2 ${isCollapsed ? 'tw-border-none' : ''}`}
                    title={
                        <div className="tw-flex tw-items-center tw-gap-2">
                            {isCollapsed ? (
                                <CaretRightIcon size="sm" className="tw-text-gray-500" />
                            ) : (
                                <CaretDownIcon size="sm" className="tw-text-gray-500" />
                            )}
                            <span className="tw-font-medium">{`${translate('resources.firewallsV2.privateAccess.preRequisites')}`}</span>
                        </div>
                    }
                />
            </div>
            {!isCollapsed && (
                <CardBody>
                    <div className="tw-flex tw-flex-col tw-gap-8">
                        <div className="tw-pt-4">
                            <div className="tw-text-sm tw-mb-1">
                                {translate('resources.firewallsV2.privateAccess.step1Title')}
                            </div>
                            <div className="tw-text-xs tw-text-gray-600 tw-mb-3">
                                <FormattedMessage
                                    id="resources.firewallsV2.privateAccess.step1Subtitle"
                                    values={{
                                        link: (
                                            <Link external href={DOCUMENTATION_URLS.AWS_RESOURCE_GATEWAY_OVERVIEW} target="_blank" rel="noopener noreferrer">
                                                {translate('generic.learnMore')}
                                            </Link>
                                        )
                                    }}
                                />
                            </div>
                            <PrerequisiteRow
                                label={translate('resources.firewallsV2.privateAccess.cloudNgfwDataplaneAccount')}
                                tooltipText={translate('resources.firewallsV2.privateAccess.cloudNgfwDataplaneAccountTooltip')}
                                addPadding
                            >
                                {firewallData?.Firewall?.DeploymentAccountId ? (
                                    <>
                                        <Body addClassName="tw-text-xs">{firewallData?.Firewall?.DeploymentAccountId}</Body>
                                        <CopyButton text={firewallData?.Firewall?.DeploymentAccountId} />
                                    </>
                                ) : null}
                            </PrerequisiteRow>
                        </div>

                        <div className="tw-pb-4 tw-border-t tw-pt-6">
                            <div className="tw-text-sm tw-mb-1">
                                {translate(isSCM
                                    ? 'resources.firewallsV2.privateAccess.step2TitleSCM'
                                    : 'resources.firewallsV2.privateAccess.step2Title')}
                            </div>
                            <div className="tw-text-xs tw-text-gray-600 tw-mb-3">
                                {translate(isSCM
                                    ? 'resources.firewallsV2.privateAccess.step2SubtitleSCM'
                                    : 'resources.firewallsV2.privateAccess.step2Subtitle')}
                            </div>

                            {isSCM ? (
                                <PrerequisiteRow
                                    label={translate('resources.firewallsV2.privateAccess.dnsServerLabel')}
                                    addPadding
                                >
                                    <Link external href={DOCUMENTATION_URLS.DNS} target="_blank" rel="noopener noreferrer">
                                        {translate('resources.firewallsV2.privateAccess.dnsServerConfigureLearnMore')}
                                    </Link>
                                </PrerequisiteRow>
                            ) : (
                                <>
                                    <PrerequisiteRow
                                        label={translate('resources.firewallsV2.privateAccess.userIdAgentLabel')}
                                        addPadding
                                    >
                                        <Link external href={DOCUMENTATION_URLS.USER_ID_AGENT} target="_blank" rel="noopener noreferrer">
                                            {translate('resources.firewallsV2.privateAccess.userIdConfigureLearnMore')}
                                        </Link>
                                    </PrerequisiteRow>
                                    <PrerequisiteRow
                                        label={translate('resources.firewallsV2.privateAccess.ldapLabel')}
                                        addPadding
                                    >
                                        <Link external href={DOCUMENTATION_URLS.LDAP} target="_blank" rel="noopener noreferrer">
                                            {translate('resources.firewallsV2.privateAccess.ldapConfigureLearnMore')}
                                        </Link>
                                    </PrerequisiteRow>
                                </>
                            )}
                        </div>
                    </div>
                </CardBody>
            )}
        </Card>
    );
};
